// deliverableAudit — deterministic static audit of a web deliverable the agent
// just wrote, so the harness can CATCH (not hope for) the gaps the live audit
// found: sub-AA text contrast, broken internal links, missing lang/alt.
//
// PURE: zero imports. Never throws — any malformed input degrades to "no finding"
// for that check. Used by agentLoop (E3) to nudge the agent to fix BEFORE it
// finishes, and unit-tested in isolation with plain { path, content } objects.

const SKIP_LINK = /^(https?:|\/\/|#|data:|mailto:|tel:|javascript:)/i;

// Parse #hex / #rgb / rgb() / rgba() → { r, g, b, a } (a in 0..1). null if unparseable.
function parseColor(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  const rgb = v.match(/rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+%?))?\s*\)/i);
  if (rgb) {
    let a = 1;
    if (rgb[4] != null) a = rgb[4].endsWith('%') ? parseFloat(rgb[4]) / 100 : parseFloat(rgb[4]);
    return { r: +rgb[1], g: +rgb[2], b: +rgb[3], a: Number.isFinite(a) ? a : 1 };
  }
  let h = v.replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
}

// Composite a (possibly translucent) foreground over an opaque background.
function compositeOver(fg, bg, extraAlpha = 1) {
  const a = Math.max(0, Math.min(1, (fg.a ?? 1) * extraAlpha));
  return { r: fg.r * a + bg.r * (1 - a), g: fg.g * a + bg.g * (1 - a), b: fg.b * a + bg.b * (1 - a) };
}

function parseHex(hex) { return parseColor(hex); }

function relLuminance({ r, g, b }) {
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * ch(r) + 0.7152 * ch(g) + 0.0722 * ch(b);
}

/**
 * WCAG contrast ratio between two colors (#rgb or #rrggbb). 1..21.
 * Returns NaN if either color can't be parsed (caller treats NaN as "unknown").
 */
export function contrastRatio(hexA, hexB) {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  if (!a || !b) return NaN;
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

// --- CSS extraction (pragmatic regex, not a full parser) ---------------------

function buildCustomProps(css) {
  const props = {};
  const re = /--([\w-]+)\s*:\s*([^;}{]+)[;}]/g;
  let m;
  while ((m = re.exec(css))) props[`--${m[1]}`] = m[2].trim();
  return props;
}

// Resolve a CSS value to a #hex, following ONE+ levels of var() (depth-bounded).
function resolveHex(value, props, depth = 0) {
  if (typeof value !== 'string' || depth > 5) return null;
  const v = value.trim();
  const direct = v.match(/#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/);
  if (direct) return `#${direct[1]}`;
  const varRef = v.match(/var\(\s*(--[\w-]+)\s*(?:,\s*([^)]+))?\)/);
  if (varRef) {
    const ref = props[varRef[1]];
    if (ref != null) {
      const resolved = resolveHex(ref, props, depth + 1);
      if (resolved) return resolved;
    }
    if (varRef[2]) return resolveHex(varRef[2], props, depth + 1); // fallback value
  }
  return null;
}

function splitRules(css) {
  const rules = [];
  const re = /([^{}]+)\{([^{}]*)\}/g;
  let m;
  while ((m = re.exec(css))) rules.push({ selector: m[1].trim(), body: m[2] });
  return rules;
}

function declValue(body, prop) {
  // last wins (CSS cascade within a rule)
  const re = new RegExp(`(?:^|[;{\\s])${prop}\\s*:\\s*([^;]+)`, 'gi');
  let m;
  let last = null;
  while ((m = re.exec(body))) last = m[1].trim();
  return last;
}

function detectPageBg(rules, props) {
  for (const { selector, body } of rules) {
    if (!/(^|[\s,])(body|html|:root)([\s.,:]|$)/i.test(selector)) continue;
    const bg = declValue(body, 'background-color') || declValue(body, 'background');
    const hex = resolveHex(bg, props);
    if (hex) return hex;
  }
  return null;
}

function ratioFromRgb(a, b) {
  if (!a || !b) return NaN;
  const la = relLuminance(a);
  const lb = relLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function auditCss(file, props, pageBg) {
  const findings = [];
  for (const { selector, body } of splitRules(file.content == null ? '' : String(file.content))) {
    const colorVal = declValue(body, 'color');
    if (colorVal) {
      const fgHex = resolveHex(colorVal, props);
      const ruleBg = resolveHex(declValue(body, 'background-color') || declValue(body, 'background'), props);
      const bg = parseColor(ruleBg || pageBg || '');
      const fg = parseColor(fgHex || '');
      if (fg && bg) {
        // Composite the text over its background using BOTH the color's own alpha
        // (rgba) AND the rule's `opacity` — the opacity-composited "muted" text is the
        // classic AA miss a raw-color check passes (e.g. opacity:0.6 footer → 3.39:1).
        const op = parseFloat(declValue(body, 'opacity'));
        const eff = compositeOver(fg, bg, Number.isFinite(op) && op >= 0 && op < 1 ? op : 1);
        const ratio = ratioFromRgb(eff, bg);
        if (Number.isFinite(ratio) && ratio < 4.5) {
          findings.push({
            type: 'contrast',
            severity: 'error',
            file: file.path,
            message: `contraste ${ratio.toFixed(2)}:1${Number.isFinite(op) && op < 1 ? ` (com opacity ${op})` : ''} (${fgHex || colorVal.trim()} sobre ${ruleBg || pageBg}) reprova WCAG AA (mín. 4.5:1).`,
          });
        }
      }
    }
    // Focus-ring suppression (WCAG 2.4.7): a `:focus` rule (NOT `:focus-visible`)
    // that kills the outline without a visible replacement leaves keyboard users
    // with no focus indicator — the exact fail E2 missed (`.form-field input:focus`).
    if (/:focus\b(?!-visible)/i.test(selector)) {
      const outline = declValue(body, 'outline');
      const outlineW = declValue(body, 'outline-width');
      const kills = (outline && /^\s*(none|0)\b/i.test(outline)) || (outlineW && /^\s*0(px|em|rem)?\b/i.test(outlineW));
      const hasReplacement = (/\bbox-shadow\s*:/i.test(body) && !/\bbox-shadow\s*:\s*none\b/i.test(body))
        || (/\bborder(-[a-z]+)?\s*:/i.test(body) && !/\bborder(-[a-z]+)?\s*:\s*none\b/i.test(body));
      if (kills && !hasReplacement) {
        findings.push({
          type: 'focus-suppressed',
          severity: 'error',
          file: file.path,
          message: `"${selector.trim()}" remove o outline do foco (outline:none/0) sem indicador alternativo — teclado fica sem foco visível (WCAG 2.4.7). Use :focus-visible ou adicione box-shadow/border.`,
        });
      }
    }
  }
  return findings;
}

// --- contrast-claim check ----------------------------------------------------
// The audit COMPUTES the real contrast. This catches the deliverable's PROSE
// (README/copy) claiming a contrast ratio that the code can't actually achieve —
// the "invented metric" failure (e.g. claiming 18.7:1 when the real max is 17.39:1,
// or an impossible >21:1). WCAG contrast is physically bounded to [1, 21].

// Largest computed text/bg contrast ratio across the deliverable's CSS (NaN if none).
function maxContrastRatio(cssFiles, props, pageBg) {
  let max = NaN;
  for (const f of cssFiles) {
    for (const { body } of splitRules(f.content == null ? '' : String(f.content))) {
      const fg = parseColor(resolveHex(declValue(body, 'color'), props) || '');
      const ruleBg = resolveHex(declValue(body, 'background-color') || declValue(body, 'background'), props);
      const bg = parseColor(ruleBg || pageBg || '');
      if (!fg || !bg) continue;
      const op = parseFloat(declValue(body, 'opacity'));
      const eff = compositeOver(fg, bg, Number.isFinite(op) && op >= 0 && op < 1 ? op : 1);
      const r = ratioFromRgb(eff, bg);
      if (Number.isFinite(r) && (!Number.isFinite(max) || r > max)) max = r;
    }
  }
  return max;
}

const CONTRAST_CONTEXT = /(contrast|contraste|ratio|raz[ãa]o|wcag|legibilidade|leg[íi]vel|\baa\b|\baaa\b)/i;
const CONTRAST_CLAIM = /(\d{1,3}(?:\.\d+)?)\s*:\s*1\b/g;
const WCAG_THRESHOLDS = new Set([3, 4.5, 7]); // stated as REQUIREMENTS, not measurements

function auditContrastClaims(textFiles, maxRatio) {
  const findings = [];
  for (const f of textFiles) {
    const content = f.content == null ? '' : String(f.content);
    for (const line of content.split(/\r?\n/)) {
      if (!CONTRAST_CONTEXT.test(line)) continue;
      CONTRAST_CLAIM.lastIndex = 0;
      let m;
      while ((m = CONTRAST_CLAIM.exec(line))) {
        const claimed = parseFloat(m[1]);
        if (!Number.isFinite(claimed) || WCAG_THRESHOLDS.has(claimed)) continue;
        if (claimed > 21.5) {
          findings.push({ type: 'contrast-claim', severity: 'error', file: f.path, message: `afirma contraste ${m[1]}:1 — impossível: o máximo físico WCAG é 21:1. Métrica inventada; remova ou corrija.` });
        } else if (Number.isFinite(maxRatio) && claimed > maxRatio + 0.5) {
          findings.push({ type: 'contrast-claim', severity: 'warn', file: f.path, message: `afirma contraste ${m[1]}:1, mas o maior contraste computado no CSS é ${maxRatio.toFixed(2)}:1 — confirme que o número não foi inventado.` });
        }
      }
    }
  }
  return findings;
}

// --- HTML checks -------------------------------------------------------------

function normalizeLink(link) {
  return String(link).trim().split('#')[0].split('?')[0].replace(/^\.?\//, '');
}

function auditHtml(file, knownSet) {
  const findings = [];
  const html = file.content == null ? '' : String(file.content);

  if (/<html[\s>]/i.test(html) && !/<html[^>]*\slang\s*=/i.test(html)) {
    findings.push({ type: 'missing-lang', severity: 'warn', file: file.path, message: '<html> sem atributo lang.' });
  }
  for (const m of html.matchAll(/<img\b[^>]*>/gi)) {
    if (!/\salt\s*=/i.test(m[0])) {
      findings.push({ type: 'missing-alt', severity: 'warn', file: file.path, message: '<img> sem atributo alt.' });
    }
  }
  // FASE-12 E2-T01 — conteúdo PARCIAL (fragmento de apply_edit, não o arquivo
  // inteiro): broken-link é inverificável (o alvo pode existir fora do fragmento
  // e nunca ter sido lido) → pula SÓ o check de link; contraste/alt/lang seguem.
  if (file.partial) return findings;
  for (const m of html.matchAll(/(?:href|src)\s*=\s*["']([^"']+)["']/gi)) {
    const raw = m[1].trim();
    if (!raw || SKIP_LINK.test(raw)) continue;
    const norm = normalizeLink(raw);
    if (!norm) continue;
    // knownSet = files written this turn ∪ files confirmed to exist on the FS
    // (read/edited this session). A link to a pre-existing repo file the agent
    // didn't rewrite is NOT broken — only flag truly-absent targets.
    const hit = [...knownSet].some((p) => p === norm || p.endsWith(`/${norm}`) || norm.endsWith(`/${p}`) || p.endsWith(norm));
    if (!hit) {
      findings.push({ type: 'broken-link', severity: 'error', file: file.path, message: `link interno "${raw}" referencia um arquivo que não existe (não foi escrito neste turno nem encontrado no worktree).` });
    }
  }
  return findings;
}

/**
 * deliverableAudit({ files }) → { ok, findings[] }
 * files = [{ path, content }]. ok === (no findings with severity 'error').
 */
export function deliverableAudit({ files, existingPaths } = {}) {
  const list = Array.isArray(files) ? files.filter((f) => f && typeof f.path === 'string') : [];
  const findings = [];
  try {
    const cssFiles = list.filter((f) => /\.css$/i.test(f.path));
    const htmlFiles = list.filter((f) => /\.html?$/i.test(f.path));
    // knownSet = files written this turn ∪ files the caller confirmed exist on the
    // FS (read/edited this session). Optional & backward-compatible: no existingPaths
    // ⇒ behaves exactly as before (only written files count).
    const extra = Array.isArray(existingPaths) ? existingPaths.filter((p) => typeof p === 'string').map(normalizeLink) : [];
    const writtenSet = new Set([...list.map((f) => normalizeLink(f.path)), ...extra]);

    const allCss = cssFiles.map((f) => (f.content == null ? '' : String(f.content))).join('\n');
    const props = buildCustomProps(allCss);
    const pageBg = detectPageBg(splitRules(allCss), props);

    for (const f of cssFiles) findings.push(...auditCss(f, props, pageBg));
    for (const f of htmlFiles) findings.push(...auditHtml(f, writtenSet));

    // Invented-metric guard: cross-check contrast ratios CLAIMED in prose
    // (README/copy/HTML text) against what the CSS can actually achieve.
    const claimFiles = list.filter((f) => /\.(html?|md|markdown|txt)$/i.test(f.path));
    findings.push(...auditContrastClaims(claimFiles, maxContrastRatio(cssFiles, props, pageBg)));

    // prefers-reduced-motion (WCAG 2.3.3, warn): animations/transitions present but
    // no reduced-motion guard → motion-sensitive users get the full effect.
    const hasMotion = /(transition|animation)\s*:|@keyframes\b/i.test(allCss) || /scroll-behavior\s*:\s*smooth/i.test(allCss);
    if (hasMotion && !/prefers-reduced-motion/i.test(allCss) && cssFiles.length) {
      findings.push({ type: 'reduced-motion', severity: 'warn', file: cssFiles[0].path, message: 'há animação/transição/scroll suave sem @media (prefers-reduced-motion: reduce) — adicione o guard.' });
    }
  } catch {
    // never throw — a broken audit must not break the turn.
    return { ok: true, findings: [] };
  }
  return { ok: findings.every((f) => f.severity !== 'error'), findings };
}
